import { useEffect, useState, type FormEvent } from "react";
import { sichererPfad } from "../../lib/weiter";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, type Persona } from "../../data";
import { useSession } from "../../app/session";
import { WebLayout } from "../../components/shell/WebLayout";
import { GoogleAnmeldung } from "../../components/GoogleAnmeldung";
import { authFehler, authFehlerArt } from "./auth";
import { PostfachPruefen } from "./PostfachPruefen";
import { CaptchaFeld, captchaConfig } from "./Captcha";
import { DEMO_PUBLIC_LINKS } from "../../data/demo/links";
import { mitSprache, t } from "../../lib/sprache";

/**
 * Sprache der Anmeldung: ?sprache=en oder die Sprache der Seite, zu der es danach weitergeht (z. B. eine englische Einladung);
 * sonst Deutsch (SICHT-OEFFENTLICH-BETRIEB-05).
 */
export function anmeldeSprache(params: URLSearchParams): "de" | "en" {
  if (params.get("sprache") === "en") return "en";
  const weiter = params.get("weiter") ?? "";
  return /[?&]sprache=en(&|$)/.test(weiter) ? "en" : "de";
}

/** Anmelden (10_website/login.html). Im Demo-Modus zusätzlich die erfundenen Demo-Personen. */
export function Anmelden() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    api.mode === "demo" && params.get("vorschau") === "unbestaetigt" ? authFehler({ code: "email_not_confirmed" }, anmeldeSprache(params)) : null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  // CAPTCHA (Befund 9): Token je Versuch; nach einem Fehler neu lösen
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  const weiter = params.get("weiter");
  // Von „Du hast schon ein Konto“ kommt die Adresse mit (Navigationszustand, nie in der URL)
  const vorgabe = (useLocation().state as { email?: string } | null)?.email;
  const lang = anmeldeSprache(params);
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  // Betreiber-Anmeldung (Inhaber 20.10.2026): von einem Betriebslink aus, während ein anderes Konto angemeldet ist. Die Anmeldung
  // ersetzt die Sitzung; danach mit neuem Laden zur aufgerufenen Betriebsseite (Konto und Arbeitsbereich frisch)
  const betrieb = params.get("betrieb") === "1";
  const { session } = useSession();
  const after = () => (betrieb ? window.location.assign(sichererPfad(weiter, "/betrieb")) : navigate(sichererPfad(weiter, "/"), { replace: true }));
  // Vertrieb 14.10.2026: Adresse noch nicht bestätigt (Code nie eingegeben) → hier neuen Code anfordern und eingeben, statt nur
  // „bitte bestätigen“ zu lesen. Danach angemeldet weiter (ohne Agentur führt „/“ zur Tarifwahl).
  // Demo-Vorschau ?vorschau=unbestaetigt: so sieht es aus, wenn die Adresse noch offen ist (wie ?vorschau=postfach beim Registrieren)
  const vorschauOffen = api.mode === "demo" && params.get("vorschau") === "unbestaetigt";
  const [offen, setOffen] = useState<string | null>(vorschauOffen ? "lena.sommer@example.com" : null);
  const [codeFuer, setCodeFuer] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const mailZiel = sichererPfad(weiter, "/einrichtung/registrierung");

  useEffect(() => {
    let alive = true;
    api.personas?.().then((p) => alive && setPersonas(p)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (mitCaptcha && !captcha) {
      setError(t("Bitte zuerst die Sicherheitsprüfung lösen.", "Please complete the security check first."));
      return;
    }
    setBusy(true);
    setError(null);
    setOffen(null);
    const mail = String(fd.get("email") ?? "").trim();
    try {
      await api.signInWithPassword(mail, String(fd.get("password") ?? ""), { captchaToken: captcha });
      after();
    } catch (err) {
      setError(authFehler(err, lang));
      if (authFehlerArt(err) === "email_not_confirmed" && mail) setOffen(mail);
      setCaptcha(null);
      setCaptchaRunde((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }
  async function neuerCode() {
    if (!offen) return;
    if (mitCaptcha && !captcha) {
      setError(t("Bitte zuerst die Sicherheitsprüfung unten lösen, dann den neuen Code anfordern.", "Please complete the security check below first, then request the new code."));
      return;
    }
    setSendet(true);
    try {
      await api.resendSignup(offen, { redirectTo: mailZiel, captchaToken: captcha });
      setError(null);
      setCodeFuer(offen);
    } catch (err) {
      setError(authFehler(err, lang));
    } finally {
      setSendet(false);
      if (mitCaptcha) { setCaptcha(null); setCaptchaRunde((n) => n + 1); }
    }
  }
  async function asPersona(id: string) {
    setBusy(true);
    try {
      await api.signInAs?.(id);
      after();
    } catch (err) {
      setError(authFehler(err));
    } finally {
      setBusy(false);
    }
  }
  const chef = personas.find((p) => p.detail.startsWith("Admin"));

  return (
    <WebLayout lang={lang} title={t("Anmelden", "Sign in")}>
      <main id="main" className="web">
        {codeFuer ? <div className="centerform"><PostfachPruefen email={codeFuer} redirectTo={mailZiel} ziel="anmeldung"
          onBack={() => { setCodeFuer(null); setOffen(null); setError(null); }} onVerified={after} /></div> : (
        <form className="centerform panel tk-auth" onSubmit={submit} aria-busy={busy}>
          <div className="tk-auth-intro">
          <h1>{betrieb ? t("Betreiber-Anmeldung", "Operator sign-in") : t("Willkommen zurück.", "Welcome back.")}</h1>
          <p>{betrieb
            ? t("Die Betreiber-Konsole öffnet sich nur mit dem Betreiber-Konto.", "The operator console opens only with the operator account.")
            : t("Melde dich in deinem Arbeitsbereich an.", "Sign in to your workspace.")}</p>
          {betrieb && session && <p className="notice" role="status">{t(`Du bist gerade als ${session.email} angemeldet. Mit der Anmeldung hier wechselst du das Konto.`, `You are currently signed in as ${session.email}. Signing in here switches the account.`)}</p>}
          </div>
          <div className="tk-auth-felder stack">
          {/* Konsole (auch als Dock-App): Weiterleitung zu Google statt Popup, danach der zweite Faktor */}
          <GoogleAnmeldung t={t} redirectPath={sichererPfad(weiter, betrieb ? "/betrieb" : "/")} nurUmleitung={betrieb} />
          <label>
            {t("E-Mail", "Email")}
            <input name="email" type="email" required autoComplete="username" defaultValue={typeof vorgabe === "string" ? vorgabe : undefined} />
          </label>
          <label>
            {t("Passwort", "Password")}
            <input name="password" type="password" autoComplete="current-password" required={api.mode !== "demo"} />
          </label>
          <Link to={mitSprache("/passwort", lang)}>{t("Passwort vergessen?", "Forgot your password?")}</Link>
          {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
          {error && <p className="tk-error" role="alert">{error}</p>}
          {offen && (
            <div className="row">
              <button type="button" onClick={() => void neuerCode()} disabled={sendet || busy}>{sendet ? t("Wird gesendet …", "Sending …") : t("Neuen Code senden", "Send new code")}</button>
              <button type="button" onClick={() => { setError(null); setCodeFuer(offen); }}>{t("Ich habe schon einen Code", "I already have a code")}</button>
            </div>
          )}
          <button className="primary" type="submit" disabled={busy}>{busy ? t("Wird angemeldet …", "Signing in …") : t("Anmelden", "Sign in")}</button>
          {api.mode === "demo" && chef && (
            <button className="btn " type="button" onClick={() => void asPersona(chef.userId)} disabled={busy}>{t("Demo öffnen", "Open demo")}</button>
          )}
          {api.mode === "demo" && <small>{t("Demo im Browser: erfundene Daten, nichts wird übertragen. Neu laden setzt die Demo zurück.", "Demo in the browser: fictional data, nothing is transmitted. Reloading resets the demo.")}</small>}
          {/* Weg zur Testphase auch in der Karte, nicht nur im Kopf (SICHT-OEFFENTLICH-BETRIEB-27) */}
          {!betrieb && <small>{t("Noch kein Konto? ", "No account yet? ")}<Link to={mitSprache("/registrieren", lang)}>{t("Kostenlos testen", "Try for free")}</Link></small>}
          </div>
        </form>
        )}
        {!codeFuer && api.mode === "demo" && personas.length > 0 && (
          <section className="centerform panel stack" aria-labelledby="demo-personen">
            <h2 id="demo-personen">{t("Demo-Zugänge", "Demo accounts")}</h2>
            <p>{t("Wähle eine Person, um die Oberfläche aus ihrer Sicht zu sehen.", "Choose a person to see the interface from their perspective.")}</p>
            {personas.map((p) => (
              <button key={p.userId} type="button" className="tk-persona" onClick={() => void asPersona(p.userId)} disabled={busy}>
                <strong>{p.label}</strong>
                <small>{p.detail || p.email}</small>
              </button>
            ))}
            <h3>{t("Öffentliche Seiten", "Public pages")}</h3>
            {DEMO_PUBLIC_LINKS.map((l) => (
              <button key={l.to} type="button" className="tk-persona" onClick={() => navigate(l.to)}>
                <strong>{l.label}</strong>
                <small>{l.detail}</small>
              </button>
            ))}
          </section>
        )}
      </main>
    </WebLayout>
  );
}
